"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUserOs = getUserOs;
exports.getUserDevice = getUserDevice;
exports.getUserBrowser = getUserBrowser;
exports.isUserOsMobile = isUserOsMobile;
exports.isSafari = isSafari;
exports.isChromiumBased = isChromiumBased;
exports.isIOS = isIOS;
exports.formattedKeyValuePairs = formattedKeyValuePairs;
exports.getNavigator = getNavigator;
exports.navigateToExternalUrl = navigateToExternalUrl;
exports.isSafeRedirectUrl = isSafeRedirectUrl;
exports.sanitizeRedirectUrl = sanitizeRedirectUrl;
const tslib_1 = require("tslib");
const isSSR_1 = tslib_1.__importDefault(require("./isSSR"));
const user_agent_1 = require("./user-agent");
function getUserOs() {
    if ((0, isSSR_1.default)()) {
        return { name: '', version: '' };
    }
    return (0, user_agent_1.getUserAgent)().os;
}
function getUserDevice() {
    if ((0, isSSR_1.default)()) {
        return { model: '', type: '', vendor: '' };
    }
    return (0, user_agent_1.getUserAgent)().device;
}
/** To see a list of possible OS names, Introspect the name property in your IDE */
function getUserBrowser() {
    if ((0, isSSR_1.default)()) {
        return { name: '', version: '', major: '' };
    }
    return (0, user_agent_1.getUserAgent)().browser;
}
function isUserOsMobile() {
    const os = getUserOs().name;
    return os === 'Android' || os === 'iOS';
}
function isSafari() {
    var _a;
    if ((0, isSSR_1.default)()) {
        return false;
    }
    return !!((_a = getUserBrowser().name) === null || _a === void 0 ? void 0 : _a.includes('Safari'));
}
function isChromiumBased() {
    if ((0, isSSR_1.default)()) {
        return false;
    }
    return (0, user_agent_1.getUserAgent)().engine.name === 'Blink';
}
function isIOS() {
    return getUserOs().name === 'iOS';
}
function formattedKeyValuePairs(headerValues = {}) {
    return Object.entries(headerValues).reduce((acc, [key, value]) => {
        if (value) {
            return `${acc}${key}=${value.replace(/[=;]/g, '')};`;
        }
        return acc;
    }, '');
}
// This helper function exists so it can be easily mocked in tests
function getNavigator() {
    if (typeof window !== 'undefined') {
        return navigator;
    }
    return {};
}
/**
 * Navigate to an external URL by setting window.location.href
 *
 * This utility exists to make external navigation testable in Jest.
 * Direct use of window.location.href is difficult to mock in Jest tests
 * because JSDOM's Location object has complex internal state that causes
 * errors when attempting to mock or stub it.
 *
 * Due to this, the function cannot be tested, but can be easily mocked and spied
 *
 * Use this for navigating to external URLs (outside the Next.js app).
 * For internal navigation within the app, use Next.js router instead.
 */
function navigateToExternalUrl(url) {
    window.location.href = url;
}
/**
 * Validates that a redirect URL is safe to use for navigation.
 *
 * Prevents open redirect attacks by only allowing relative paths (starting with `/`)
 * or absolute URLs that share the same origin as the current page.
 *
 * @param url - The URL to validate
 * @returns True if the URL is safe to redirect to
 *
 * @example
 * isSafeRedirectUrl('/profile/settings') // true
 * isSafeRedirectUrl('https://evil.com') // false
 * isSafeRedirectUrl('https://app-staging.calmhealth.site/plan') // true (same origin)
 */
function isSafeRedirectUrl(url) {
    if (!url)
        return false;
    if (url.startsWith('/') && !url.startsWith('//'))
        return true;
    try {
        const parsed = new URL(url);
        return parsed.origin === window.location.origin;
    }
    catch (_a) {
        return false;
    }
}
/**
 * Returns a sanitized redirect URL, falling back to a safe default if the
 * provided URL would redirect to an external domain.
 *
 * @param url - The redirect URL to sanitize
 * @param fallback - Safe fallback URL (must be a relative path)
 * @returns The original URL if safe, otherwise the fallback
 *
 * @example
 * sanitizeRedirectUrl('/plan', '/') // '/plan'
 * sanitizeRedirectUrl('https://evil.com', '/') // '/'
 * sanitizeRedirectUrl('', '/dashboard') // '/dashboard'
 */
function sanitizeRedirectUrl(url, fallback) {
    return isSafeRedirectUrl(url) ? url : fallback;
}
//# sourceMappingURL=browser.js.map